/**
 * One exercise's prescription as one line of facts — "4 × 6 @RPE 8 · +2,5 kg / cykl" before it is
 * worded: the sets it prescribes, what they ask for (repetitions or time, load, effort, rest), and
 * the rule that moves it on. What a proposal change says an exercise was and becomes
 * (ai-plan-builder R15).
 *
 * A DESCRIPTION shows every set: a warm-up exercise reads "2 × 15", and warm-up sets in front of the
 * work read as "rozgrzewka 1 + 3 × 10". What counts as work is still the counting rule
 * (`setCounting.ts`, `!= Warmup`): the work is the counted sets, and the values are read off them.
 * Values that differ from set to set are kept in order ("10/8/6"), never averaged.
 *
 * Only what is PRESCRIBED is read (`prescribedColumns`): a column the trainer merely opened is empty
 * and says nothing.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. The words are the caller's.
 */

import type { MeasurementFieldKey } from '~/shared/measurement';

import { prescribedColumns } from './exerciseColumns';
import type { ExerciseDraft, SetDraft } from './planDraft.types';
import { isPercentMode } from './prescriptionMode';
import type { ProgressionDeltaUnit, ProgressionRule } from './progressionRules';
import { countsAsWork } from './setCounting';

/** The fields a set's size is told in, in the order the line looks for one. */
const VOLUME_FIELDS = ['reps', 'duration', 'distance', 'rounds'] as const;

export type VolumeField = (typeof VOLUME_FIELDS)[number];

/** One value of the line, set by set, in the sets' order. */
export interface LineValues<F> {
  readonly field: F;
  readonly values: readonly number[];
}

export interface PrescriptionLine {
  /** Warm-up sets in front of the work; `0` when the sets described are all of one kind. */
  readonly warmups: number;
  /** The sets described: the work, or every set when none counts as work (a warm-up exercise). */
  readonly sets: number;
  /** What each set asks for: repetitions, seconds, metres or rounds. */
  readonly volume: LineValues<VolumeField> | null;
  /** The load of each set, in kilograms or as a fraction of the maximum (0–1). */
  readonly load: LineValues<'kg' | 'percent'> | null;
  readonly effort: LineValues<'rpe' | 'rir'> | null;
  /** The rest after each set, in seconds. */
  readonly rest: LineValues<'rest'> | null;
  /** The first field a rule advances, by how much a cycle; `null` when nothing progresses. */
  readonly progression: {
    readonly field: MeasurementFieldKey;
    readonly delta: number;
    readonly unit: ProgressionDeltaUnit;
  } | null;
}

const VALUE: Readonly<Record<VolumeField | 'rpe' | 'rir', (set: SetDraft) => number | null>> = {
  reps: (set) => set.prescribedReps,
  duration: (set) => set.prescribedDurationSeconds,
  distance: (set) => set.prescribedDistanceMeters,
  rounds: (set) => set.prescribedRounds,
  rpe: (set) => set.prescribedRpe,
  rir: (set) => set.prescribedRir,
};

/** The first field a Static rule moves on by a delta. A rule resolved from the client's own history has no number to state. */
function progressionOf(exercise: ExerciseDraft): PrescriptionLine['progression'] {
  const rules = Object.entries(exercise.progressionRules) as [MeasurementFieldKey, ProgressionRule | undefined][];
  for (const [field, rule] of rules) {
    if (rule?.type === 'Static' && rule.delta !== null) return { field, delta: rule.delta, unit: rule.deltaUnit };
  }
  return null;
}

/** The values a reader gives for each set, or `null` when no set holds one. */
function valuesOf<F>(field: F, sets: readonly SetDraft[], read: (set: SetDraft) => number | null): LineValues<F> | null {
  const values = sets.map(read).filter((value): value is number => value !== null);
  return values.length === 0 ? null : { field, values };
}

/** The first of `fields` the exercise prescribes and some set holds a value for. */
function firstOf<F extends VolumeField | 'rpe' | 'rir'>(
  fields: readonly F[],
  columns: ReadonlySet<MeasurementFieldKey>,
  sets: readonly SetDraft[],
): LineValues<F> | null {
  for (const field of fields) {
    if (!columns.has(field)) continue;
    const found = valuesOf(field, sets, VALUE[field]);
    if (found !== null) return found;
  }
  return null;
}

export function prescriptionLine(exercise: ExerciseDraft): PrescriptionLine {
  const columns = new Set(prescribedColumns(exercise));
  const work = exercise.sets.filter((set) => countsAsWork(set.setType));
  // A warm-up exercise is described by its own sets: there is no work to stand for it.
  const described = work.length > 0 ? work : exercise.sets;
  // The load cell the exercise's mode prescribes: kilograms, or a fraction of the maximum.
  const load = !columns.has('weight')
    ? null
    : isPercentMode(exercise.prescriptionMode)
      ? valuesOf('percent' as const, described, (set) => set.prescribedPercent)
      : valuesOf('kg' as const, described, (set) => set.prescribedWeight);
  return {
    warmups: work.length > 0 ? exercise.sets.length - work.length : 0,
    sets: described.length,
    volume: firstOf(VOLUME_FIELDS, columns, described),
    load,
    effort: firstOf(['rpe', 'rir'] as const, columns, described),
    rest: valuesOf('rest' as const, described, (set) => set.prescribedRestTime),
    progression: progressionOf(exercise),
  };
}
